import React, { useState, useEffect, useCallback } from 'react';
import { cidadesApi } from '../lib/api';
import { ESTADOS_BR, getEstadoNome } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, MapPin, Pencil, X } from 'lucide-react';

const CidadesAtendimento = () => {
  const [cidades, setCidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [novaCidade, setNovaCidade] = useState('');
  const [novoEstado, setNovoEstado] = useState('MG');
  const [novaRegiao, setNovaRegiao] = useState('');
  const [saving, setSaving] = useState(false);
  const [editCidade, setEditCidade] = useState(null);
  const [zonaInputs, setZonaInputs] = useState({});

  const loadCidades = useCallback(async () => {
    try {
      const r = await cidadesApi.list();
      setCidades(r.data);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar cidades');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCidades(); }, [loadCidades]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const nome = novaCidade.trim();
    if (!nome) {
      toast.error('Informe o nome da cidade');
      return;
    }
    setSaving(true);
    try {
      await cidadesApi.create({ nome, estado: novoEstado, regiao_metropolitana: novaRegiao.trim() });
      toast.success('Cidade adicionada');
      setNovaCidade('');
      setNovaRegiao('');
      loadCidades();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao adicionar cidade');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cidade) => {
    if (!window.confirm(`Remover "${cidade.nome}" das cidades atendidas?`)) return;
    try {
      await cidadesApi.delete(cidade.id);
      toast.success('Cidade removida');
      loadCidades();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao remover cidade');
    }
  };

  const handleAddZona = async (cidade) => {
    const zona = (zonaInputs[cidade.id] || '').trim();
    if (!zona) {
      toast.error('Informe o nome da zona/região');
      return;
    }
    const atuais = cidade.zonas || [];
    if (atuais.some((z) => z.nome.toLowerCase() === zona.toLowerCase())) {
      toast.error('Zona já cadastrada nesta cidade');
      return;
    }
    try {
      await cidadesApi.update(cidade.id, { zonas: [...atuais, { nome: zona, bairros: [] }] });
      setZonaInputs((prev) => ({ ...prev, [cidade.id]: '' }));
      toast.success(`Zona "${zona}" adicionada em ${cidade.nome}`);
      loadCidades();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao adicionar zona');
    }
  };

  const handleRemoveZona = async (cidade, zonaNome) => {
    if (!window.confirm(`Remover a zona "${zonaNome}" de ${cidade.nome}?`)) return;
    try {
      await cidadesApi.update(cidade.id, {
        zonas: (cidade.zonas || []).filter((z) => z.nome !== zonaNome),
      });
      toast.success('Zona removida');
      loadCidades();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao remover zona');
    }
  };

  const handleSaveEdit = async () => {
    if (!editCidade?.nome?.trim()) {
      toast.error('Informe o nome da cidade');
      return;
    }
    try {
      await cidadesApi.update(editCidade.id, {
        nome: editCidade.nome.trim(),
        estado: editCidade.estado,
        regiao_metropolitana: (editCidade.regiao_metropolitana || '').trim(),
      });
      toast.success('Cidade atualizada');
      setEditCidade(null);
      loadCidades();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao atualizar cidade');
    }
  };

  return (
    <div className="space-y-6" data-testid="cidades-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Work Sans' }}>Cidades de Atendimento</h1>
        <p className="text-gray-600 mt-1">
          Estrutura: <b>Estado → Cidade → Zonas/Regiões</b>. Cidades grandes (ex: São Paulo) podem ter
          várias zonas. Cidades sem zonas funcionam normalmente. A classificação (ex: "Capital", "ABC",
          "Litoral") é opcional e serve apenas para organização.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
            <Plus size={20} className="text-emerald-600" />
            Adicionar nova cidade
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1 w-full">
              <Label htmlFor="nova-cidade">Nome da cidade *</Label>
              <Input
                id="nova-cidade"
                placeholder="Ex: Guarulhos"
                value={novaCidade}
                onChange={(e) => setNovaCidade(e.target.value)}
                data-testid="input-nova-cidade"
                disabled={saving}
              />
            </div>
            <div className="w-full sm:w-48">
              <Label>Estado (UF) *</Label>
              <Select value={novoEstado} onValueChange={setNovoEstado} disabled={saving}>
                <SelectTrigger data-testid="select-novo-estado">
                  <SelectValue placeholder="UF" />
                </SelectTrigger>
                <SelectContent>
                  {ESTADOS_BR.map((e) => (
                    <SelectItem key={e.uf} value={e.uf}>{e.uf} — {e.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-56">
              <Label>Classificação (opcional)</Label>
              <Input
                placeholder="Ex: Capital, ABC, Litoral..."
                value={novaRegiao}
                onChange={(e) => setNovaRegiao(e.target.value)}
                data-testid="input-nova-regiao"
                disabled={saving}
              />
            </div>
            <Button
              type="submit"
              disabled={saving}
              className="text-white"
              style={{ backgroundColor: '#2B7A3D' }}
              data-testid="btn-add-cidade"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus size={16} className="mr-1" />}
              Adicionar
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
            <MapPin size={20} className="text-emerald-600" />
            Cidades cadastradas
            <span className="text-sm text-gray-500 font-normal">({cidades.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : cidades.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhuma cidade cadastrada. Adicione a primeira acima.
            </div>
          ) : (
            <div className="space-y-6">
              {[...new Set(cidades.map((c) => c.estado || 'MG'))].sort().map((uf) => (
                <div key={uf}>
                  <p className="text-sm font-bold text-gray-800 mb-2">{getEstadoNome(uf)} ({uf})</p>
                  <div className="space-y-2">
                    {cidades.filter((c) => (c.estado || 'MG') === uf).map((c) => (
                      <div
                        key={c.id}
                        className="rounded-lg border border-gray-200 p-3"
                        data-testid={`cidade-item-${c.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 text-emerald-800 flex-wrap">
                            <MapPin size={15} />
                            <span className="font-medium">{c.nome}</span>
                            {c.regiao_metropolitana && (
                              <span className="text-xs rounded-full bg-gray-100 border border-gray-200 px-2 py-0.5 text-gray-600">
                                {c.regiao_metropolitana}
                              </span>
                            )}
                            {(c.zonas || []).length > 0 && (
                              <span className="text-xs text-gray-400">({c.zonas.length} zonas)</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setEditCidade({ ...c })}
                              className="text-gray-400 hover:text-gray-700"
                              title="Editar cidade"
                              data-testid={`btn-edit-cidade-${c.id}`}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(c)}
                              className="text-red-500 hover:text-red-700"
                              title="Remover cidade"
                              data-testid={`btn-del-cidade-${c.id}`}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {(c.zonas || []).map((z) => (
                            <span
                              key={z.nome}
                              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs text-emerald-800"
                              data-testid={`zona-chip-${c.id}-${z.nome}`}
                            >
                              {z.nome}
                              <button
                                type="button"
                                onClick={() => handleRemoveZona(c, z.nome)}
                                className="text-red-400 hover:text-red-600"
                                title={`Remover zona ${z.nome}`}
                                data-testid={`btn-del-zona-${c.id}-${z.nome}`}
                              >
                                <X size={11} />
                              </button>
                            </span>
                          ))}
                          <div className="flex items-center gap-1.5">
                            <Input
                              value={zonaInputs[c.id] || ''}
                              onChange={(e) => setZonaInputs((prev) => ({ ...prev, [c.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddZona(c); } }}
                              placeholder="Adicionar zona/região..."
                              className="h-7 w-44 text-xs"
                              data-testid={`input-zona-${c.id}`}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => handleAddZona(c)}
                              data-testid={`btn-add-zona-${c.id}`}
                            >
                              <Plus size={12} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editCidade} onOpenChange={(o) => { if (!o) setEditCidade(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>Editar cidade</DialogTitle>
          </DialogHeader>
          {editCidade && (
            <div className="space-y-3">
              <div>
                <Label>Nome da cidade *</Label>
                <Input
                  value={editCidade.nome}
                  onChange={(e) => setEditCidade({ ...editCidade, nome: e.target.value })}
                  data-testid="edit-cidade-nome"
                />
              </div>
              <div>
                <Label>Estado (UF) *</Label>
                <Select
                  value={editCidade.estado}
                  onValueChange={(v) => setEditCidade({ ...editCidade, estado: v })}
                >
                  <SelectTrigger data-testid="edit-cidade-estado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTADOS_BR.map((e) => (
                      <SelectItem key={e.uf} value={e.uf}>{e.uf} — {e.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Classificação (opcional)</Label>
                <Input
                  value={editCidade.regiao_metropolitana || ''}
                  onChange={(e) => setEditCidade({ ...editCidade, regiao_metropolitana: e.target.value })}
                  placeholder="Ex: Capital, ABC, Litoral..."
                  data-testid="edit-cidade-regiao"
                />
              </div>
              <p className="text-xs text-gray-500">
                As zonas são gerenciadas diretamente na lista (chips). Renomear a cidade atualiza
                automaticamente OS, orçamentos, colaboradores e leads vinculados.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCidade(null)}>Cancelar</Button>
            <Button
              className="text-white"
              style={{ backgroundColor: '#2B7A3D' }}
              onClick={handleSaveEdit}
              data-testid="btn-save-edit-cidade"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CidadesAtendimento;
